import { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAccounts } from 'apis/accounts.api';
import {
	tokenSelector,
	userAccountsSelector,
	accountsPaginationSelector,
	accountsLoadingSelector
} from 'reducers/userAuthSlice';
import { setCurrentAccountId } from 'reducers/userAuthSlice';
import {
	CURRENT_ACCOUNT_ID,
	COGNITO_CONFIG,
	INTENDED_LOCATION
} from 'constants/localStorageConstants';
import { globalIsLoadingSelector, addSnackbarAlerts } from 'reducers/globalSlice';
import { handleAwsCognito } from 'services/aws-cognito';
import queryString from 'query-string';
import { LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

const PostCognito = () => {
	const { t } = useTranslation();
	const token = useSelector(tokenSelector);
	const isLoading = useSelector(globalIsLoadingSelector);
	const [accountsLoaded, setAccountsLoaded] = useState(false);
	const cognitoConfig = useMemo(() => JSON.parse(localStorage.getItem(COGNITO_CONFIG)), []);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const accounts = useSelector(userAccountsSelector);
	const accountsPagination = useSelector(accountsPaginationSelector);
	const accountsLoading = useSelector(accountsLoadingSelector);

	const isAccountExpired = account => {
		return account?.subscription_status === 'enterprise_expired';
	};

	const directUserAfterLogin = useCallback(
		accounts => {
			const intendedLocation = localStorage.getItem(INTENDED_LOCATION);
			if (intendedLocation) {
				//Go to the link the user initially visited before having to go thru the login flow
				navigate(intendedLocation);
				localStorage.removeItem(INTENDED_LOCATION);
				return;
			} else {
				switch (accounts.length) {
					case null: {
						return;
					}
					case 0: {
						dispatch(
							addSnackbarAlerts([
								{
									type: 'error',
									message: `${t('No accounts founds')}.`
								}
							])
						);
						return navigate('/no-accounts');
					}
					case 1: {
						if (isAccountExpired(accounts[0])) {
							dispatch(
								addSnackbarAlerts([
									{
										type: 'error',
										message: `${t('Your account subscription has expired')}.`
									}
								])
							);
							return navigate('/no-accounts');
						} else {
							localStorage.setItem(CURRENT_ACCOUNT_ID, accounts[0].id);
							dispatch(setCurrentAccountId(accounts[0].id));
							return navigate(`/account/${accounts[0].id}/airspace`);
						}
					}
					default: {
						return navigate('/account-picker');
					}
				}
			}
		},
		[dispatch, navigate, t]
	);

	useEffect(() => {
		const queryParameters = queryString.parse(window.location.search);

		if (token) {
			const getAccounts = async page => {
				await dispatch(fetchAccounts(page));
			};
			if (!accounts && !accountsLoaded && !accountsLoading) {
				// Get first set of accounts
				getAccounts(1);
			} else if (
				accounts &&
				!accountsLoaded &&
				!accountsLoading &&
				accountsPagination?.current_page !== accountsPagination?.last_page &&
				accountsPagination?.last_page !== 0
			) {
				// Incrementally load all pages of accounts
				getAccounts(accountsPagination.current_page + 1);
			} else if (accounts && !accountsLoading) {
				directUserAfterLogin(accounts);
				setAccountsLoaded(true);
			} else {
				setAccountsLoaded(true);
			}
		} else {
			if (queryParameters.error) {
				//If cognito returns an error query param, go to login page and display that error
				return navigate({
					pathname: '/login',
					search: queryString.stringify(queryParameters)
				});
			} else {
				if (cognitoConfig) {
					//If the login form called the authorize endpoint it will redirect to this component
					// and then the token endpoint will be called
					handleAwsCognito(cognitoConfig);
				} else {
					//Config is not present in local storage - redirect to login
					//Occurs when user is not logged in and goes directly to /post-login-flow
					window.location.assign(`${origin}/login`);
				}
			}
		}
	}, [
		accounts,
		accountsLoaded,
		accountsLoading,
		accountsPagination,
		cognitoConfig,
		directUserAfterLogin,
		dispatch,
		navigate,
		token
	]);

	return <div style={{ height: '100%' }}>{isLoading && <LinearProgress />}</div>;
};

export default PostCognito;
